import { Route, Routes } from 'react-router-dom';
import { AuthenticationGuard } from '../modules/auth/guards/ProtectedRoute';
import { RegisterRedirect } from '../modules/auth/RegisterRedirect';
import {
  Dashboard,
  FileUpload,
  InvoiceConfirmDataPage,
  InvoiceDetail,
  InvoiceEndRemindingPage,
  InvoiceUpdateAmountPage,
  Login,
  Logout,
  UserProfile,
} from '../pages';
import { AdminClaimDetail } from '../pages/admin/claim-detail';
import { AdminClaimsPage } from '../pages/admin/claims';
import { CompletedScheduledActions } from '../pages/admin/completed-scheduled-actions';
import { AdminInvoiceDetail } from '../pages/admin/invoice-detail';
import { Invoices as AdminInvoices } from '../pages/admin/invoices';
import { ReceivedMessages } from '../pages/admin/received-messages';
import { ScheduledActionDetail } from '../pages/admin/scheduled-action-detail';
import { ScheduledActions } from '../pages/admin/scheduled-actions';
import { DataMapperPage } from '../pages/admin/universal-reminding/data-mapper';
import { ScenariosPage } from '../pages/admin/universal-reminding/scenarios';
import { DeleteScenarioPage } from '../pages/admin/universal-reminding/scenarios/delete';
import { ScenarioDetailPage } from '../pages/admin/universal-reminding/scenarios/detail';
import { DuplicateScenarioPage } from '../pages/admin/universal-reminding/scenarios/duplicate';
import { EditScenarioPage } from '../pages/admin/universal-reminding/scenarios/edit';
import { CreateScenarioPage } from '../pages/admin/universal-reminding/scenarios/new';
import { UniversalTemplatePage } from '../pages/admin/universal-reminding/universal-template';
import { AdminUsersPage } from '../pages/admin/users';
import { BankConnection } from '../pages/bank-connection';
import { ConnectBankPage } from '../pages/bank-connection/connect';
import { CreateBankConnectionPage } from '../pages/bank-connection/create';
import { DisconnectBankPage } from '../pages/bank-connection/disconnect';
import { ClaimDetail } from '../pages/claim/detail';
import { ClaimEndRemindingPage } from '../pages/claim/end-reminding';
import { EscalateClaimPage } from '../pages/claim/escalate';
import { ClaimSetPaidPage } from '../pages/claim/set-paid';
import { ClaimUpdateAmountPage } from '../pages/claim/update-amount';
import { ContactsPage } from '../pages/contacts';
import { CustomersOverview } from '../pages/contacts/customers';
import { EditCustomer } from '../pages/contacts/edit/EditCustomer';
import { EditVendor } from '../pages/contacts/edit/EditVendor';
import { VendorsOverview } from '../pages/contacts/vendors';
import { Claims } from '../pages/dashboard/claims';
import { Invoices } from '../pages/dashboard/Invoices';
import { GeneralErrorPage } from '../pages/error/GeneralErrorPage';
import { NotFoundError } from '../pages/error/NotFoundError';
import { EscalateInvoicePage } from '../pages/invoice/escalate';
import { InvoiceSetPaidPage } from '../pages/invoice/set-paid';
import { InvoiceManagementInvoicesPage } from '../pages/invoice-management/invoices';
import { InvoiceManagementCopyInvoicePage } from '../pages/invoice-management/invoices/copy';
import { InvoiceManagementCreateInvoicePage } from '../pages/invoice-management/invoices/create';
import { InvoiceManagementEditInvoicePage } from '../pages/invoice-management/invoices/edit';
import { InvoiceManagementInvoiceDetail } from '../pages/invoice-management/invoices/invoice-detail';
import { InvoiceManagementStartRemindingPage } from '../pages/invoice-management/invoices/start-reminding';
import { RegistrationRemindingTonePage } from '../pages/registration-reminding-tone';
import { RemindingTonePage } from '../pages/reminding-tone';
import { Support } from '../pages/support';
import { BatchBilling } from '../pages/upload/batch/billing';
import { ConfirmInvoiceDrawerContent } from '../pages/upload/batch/confirm-invoice';
import { BatchPaymentResult } from '../pages/upload/batch/payment-result';
import { BatchUploadSummaryPage } from '../pages/upload/batch/summary';
import { ClaimsCreateBatch } from '../pages/upload/claims/create-batch';
import { ClaimsSummaryPage } from '../pages/upload/claims/summary';
import { InvoiceAnnotationLoading } from '../pages/upload/invoice/annotation-loading';
import { InvoiceBilling } from '../pages/upload/invoice/billing';
import { NewInvoicePage } from '../pages/upload/invoice/new';
import { InvoicePaymentResult } from '../pages/upload/invoice/payment-result';
import { UnsupportedFile } from '../pages/upload/unsupported-file';
import { XMLCompleteDataPage } from '../pages/upload/xml/xml-complete-data';
import { XmlCreateBatch } from '../pages/upload/xml/xml-create-batch';
import { XMLSummaryPage } from '../pages/upload/xml/xml-summary';
import { XmlUpsertContacts } from '../pages/upload/xml/xml-upsert-contacts';
import { ChangePassword } from '../pages/user-profile/change-password/ChangePassword';
import { UserRegistration } from '../pages/user-registration';
import { AdminRootComponent, ClientRootComponent } from '../RootComponent';
import { ModalRoute } from './ModalRoute';

export const AppRouter = () => {
  return (
    <Routes>
      {/* Client Routes */}
      <Route path={'/'} element={<AuthenticationGuard component={ClientRootComponent} />}>
        <Route index element={<Dashboard />} />
        <Route path={'invoices'} element={<Invoices />} />
        <Route path={'claims'} element={<Claims />} />
        <Route path={'support'} element={<Support />} />
        <Route path={'reminding-tone'} element={<RemindingTonePage />} />
        <Route path={'bank-connection'} element={<BankConnection />}>
          <Route
            path={'create'}
            element={
              <ModalRoute>
                <CreateBankConnectionPage />
              </ModalRoute>
            }
          />
          <Route
            path={'connect/:paymentProvider'}
            element={
              <ModalRoute>
                <ConnectBankPage />
              </ModalRoute>
            }
          />
          <Route
            path={'disconnect/:bankConnectionId'}
            element={
              <ModalRoute>
                <DisconnectBankPage />
              </ModalRoute>
            }
          />
        </Route>
        <Route path={'contacts'} element={<ContactsPage />}>
          <Route index element={<CustomersOverview />} />
          <Route path={'customers'} element={<CustomersOverview />}>
            <Route
              path={'edit/:id'}
              element={
                <ModalRoute>
                  <EditCustomer />
                </ModalRoute>
              }
            />
          </Route>
          <Route path={'vendors'} element={<VendorsOverview />}>
            <Route
              path={'edit/:id'}
              element={
                <ModalRoute>
                  <EditVendor />
                </ModalRoute>
              }
            />
          </Route>
        </Route>
        <Route path={'user-profile'} element={<UserProfile />}>
          <Route
            path={'reset-password'}
            element={
              <ModalRoute>
                <ChangePassword />
              </ModalRoute>
            }
          />
        </Route>

        <Route path={'claim/:invoiceId'} element={<ClaimDetail />}>
          <Route
            path={'update-amount'}
            element={
              <ModalRoute>
                <ClaimUpdateAmountPage />
              </ModalRoute>
            }
          />
          <Route
            path={'end-reminding'}
            element={
              <ModalRoute>
                <ClaimEndRemindingPage />
              </ModalRoute>
            }
          />
          <Route
            path={'set-paid'}
            element={
              <ModalRoute>
                <ClaimSetPaidPage />
              </ModalRoute>
            }
          />
          <Route
            path={'escalate'}
            element={
              <ModalRoute>
                <EscalateClaimPage />
              </ModalRoute>
            }
          />
        </Route>

        {/* Upload Routes */}
        <Route path={'upload'}>
          <Route path={''} element={<FileUpload />}>
            <Route path={'unsupported/:fileId'} element={<UnsupportedFile />} />
          </Route>
          <Route path={'invoice/:invoiceId'}>
            <Route index element={<InvoiceAnnotationLoading />} />
            <Route path={'annotation-loading'} element={<InvoiceAnnotationLoading />} />
            <Route path={'confirm'} element={<InvoiceConfirmDataPage />} />
            <Route path={'billing'} element={<InvoiceBilling />} />
            <Route path={'payment-result'} element={<InvoicePaymentResult />} />
          </Route>
          <Route path={'xml'}>
            <Route index element={<XMLSummaryPage />} />
            <Route path={'summary'} element={<XMLSummaryPage />} />
            <Route path={'complete-data'} element={<XMLCompleteDataPage />} />
            <Route path={'create-batch'} element={<XmlCreateBatch />} />
            <Route path={'upsert-contacts'} element={<XmlUpsertContacts />} />
          </Route>
          <Route path={'batch/:batchId'} element={<BatchUploadSummaryPage />}>
            <Route path={'confirm/:invoiceId'} element={<ConfirmInvoiceDrawerContent />} />
          </Route>
          <Route path={'batch/:batchId/billing'} element={<BatchBilling />} />
          <Route path={'batch/:batchId/payment-result'} element={<BatchPaymentResult />} />
          <Route path={'claims'}>
            <Route index element={<ClaimsSummaryPage />} />
            <Route path={'summary'} element={<ClaimsSummaryPage />} />
            <Route path={'create-batch'} element={<ClaimsCreateBatch />} />
          </Route>
        </Route>

        <Route path={'invoice-management'}>
          <Route index element={<InvoiceManagementInvoicesPage />} />
          <Route path={'invoices'} element={<InvoiceManagementInvoicesPage />} />
          <Route path={'invoices/create'} element={<InvoiceManagementCreateInvoicePage />} />
          <Route path={'invoices/:invoiceId'} element={<InvoiceManagementInvoiceDetail />} />
          <Route path={'invoices/:invoiceId/start-reminding'} element={<InvoiceManagementStartRemindingPage />} />
          <Route path={'invoices/:invoiceId/confirm-data'} element={<InvoiceConfirmDataPage />} />
          <Route
            path={'invoices/:invoiceId/set-paid'}
            element={
              <ModalRoute>
                <InvoiceSetPaidPage />
              </ModalRoute>
            }
          />
          <Route path={'invoices/:invoiceId/edit'} element={<InvoiceManagementEditInvoicePage />} />
          <Route path={'invoices/:invoiceId/copy'} element={<InvoiceManagementCopyInvoicePage />} />
        </Route>

        <Route path={'new'}>
          <Route path={'invoice'} element={<NewInvoicePage />} />
          <Route path={'invoice/:invoiceId'} element={<NewInvoicePage />} />
          <Route path={'invoice/:invoiceId/start-reminding'} element={<InvoiceManagementStartRemindingPage />} />
        </Route>

        {/* Invoice Routes */}
        <Route path={'invoice'}>
          <Route index element={<FileUpload />} />
          <Route path={':invoiceId'} element={<InvoiceDetail />}>
            <Route
              path={'update-amount'}
              element={
                <ModalRoute>
                  <InvoiceUpdateAmountPage />
                </ModalRoute>
              }
            />
            <Route
              path={'end-reminding'}
              element={
                <ModalRoute>
                  <InvoiceEndRemindingPage />
                </ModalRoute>
              }
            />
            <Route
              path={'set-paid'}
              element={
                <ModalRoute>
                  <InvoiceSetPaidPage />
                </ModalRoute>
              }
            />
            <Route
              path={'escalate'}
              element={
                <ModalRoute>
                  <EscalateInvoicePage />
                </ModalRoute>
              }
            />
          </Route>
          <Route path={':invoiceId/billing'} element={<InvoiceBilling />} />
        </Route>
        <Route path={'*'} element={<NotFoundError />} />
      </Route>

      {/* Admin Routes */}
      <Route path={'/admin'} element={<AuthenticationGuard component={AdminRootComponent} />}>
        <Route index element={<ScheduledActions />} />
        <Route path={'scheduled-action/:taskId'} element={<ScheduledActionDetail />} />
        <Route path={'invoice/:invoiceId'} element={<AdminInvoiceDetail />} />
        <Route path={'claim/:invoiceId'} element={<AdminClaimDetail />} />
        <Route path={'completed-scheduled-actions'}>
          <Route index element={<CompletedScheduledActions />} />
          <Route path={'scheduled-action/:taskId'} element={<ScheduledActionDetail />} />
          <Route path={'invoice/:invoiceId'} element={<AdminInvoiceDetail />} />
          <Route path={'claim/:invoiceId'} element={<AdminClaimDetail />} />
        </Route>
        <Route path={'received-messages'} element={<ReceivedMessages />} />
        <Route path={'invoices'}>
          <Route index element={<AdminInvoices />} />
          <Route path={':invoiceId'} element={<AdminInvoiceDetail />} />
        </Route>
        <Route path={'claims'}>
          <Route index element={<AdminClaimsPage />} />
          <Route path={':invoiceId'} element={<AdminClaimDetail />} />
        </Route>
        <Route path={'users'}>
          <Route index element={<AdminUsersPage />} />
        </Route>
        <Route path={'universal-reminding'}>
          <Route path={'template'} element={<UniversalTemplatePage />} />
          <Route path={'scenarios'} element={<ScenariosPage />}>
            <Route
              path={'delete/:scenarioId'}
              element={
                <ModalRoute>
                  <DeleteScenarioPage />
                </ModalRoute>
              }
            />
          </Route>
          <Route path={'scenarios'}>
            <Route index={true} element={<ScenariosPage />} />
            <Route path={'new'} element={<CreateScenarioPage />} />
            <Route path={':scenarioId'} element={<ScenarioDetailPage />}>
              <Route
                path={'delete'}
                element={
                  <ModalRoute>
                    <DeleteScenarioPage />
                  </ModalRoute>
                }
              />
            </Route>
            <Route path={'duplicate/:scenarioId'} element={<DuplicateScenarioPage />} />
            <Route path={'edit/:scenarioId'} element={<EditScenarioPage />} />
          </Route>
          <Route path={'data-mapper'} element={<DataMapperPage />} />
        </Route>

        <Route path={'*'} element={<NotFoundError />} />
      </Route>

      {/* App Shared Routes */}
      <Route path={'/user-registration'} element={<AuthenticationGuard component={UserRegistration} />} />
      <Route
        path={'/registration-reminding-tone'}
        element={<AuthenticationGuard component={RegistrationRemindingTonePage} />}
      />
      <Route path={'/login'} element={<Login />} />
      <Route path={'/register'} element={<RegisterRedirect />} />
      <Route path={'/logout'} element={<Logout />} />
      <Route path={'/error'} element={<GeneralErrorPage />} />

      <Route path={'*'} element={<NotFoundError />} />
    </Routes>
  );
};
